import { TriangleAlert } from 'lucide-react';
import type { JornadaChofer } from '@tms/contratos';
import { EXCESO_CRITICO_H, LIMITE_CONDUCCION_H } from '../config/jornada';
import { fmtHora, fmtHorasDecimal, fmtKm, fmtNum, minutosDelDia } from '../util/format';

const MARCAS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

/** Eje horario 00 a 24 con marcas cada 3 horas. */
export function EjeHoras() {
  return (
    <div className="ejes" aria-hidden>
      {MARCAS.map((h) => (
        <span key={h} style={{ left: `${(h / 24) * 100}%` }}>
          {String(h).padStart(2, '0')}
        </span>
      ))}
    </div>
  );
}

export function nivelExceso(horas: number): 'ok' | 'amber' | 'red' {
  if (horas > LIMITE_CONDUCCION_H + EXCESO_CRITICO_H) return 'red';
  if (horas > LIMITE_CONDUCCION_H) return 'amber';
  return 'ok';
}

/** Barra horizontal de 24 h con los tramos de conducción y detención de una jornada. */
export function BarraJornada({ jornada, conEje = true }: { jornada: JornadaChofer; conEje?: boolean }) {
  const nivel = nivelExceso(jornada.horasConduccion);
  const descripcion = `${jornada.nombre ?? jornada.llaveOperador}: ${fmtHorasDecimal(jornada.horasConduccion)} de conducción, ${fmtKm(jornada.km)}`;
  return (
    <div>
      <div className={`barra24 ${nivel === 'ok' ? '' : `excede-${nivel}`}`} role="img" aria-label={descripcion}>
        {MARCAS.slice(1, -1).map((h) => (
          <span key={h} className="marca" style={{ left: `${(h / 24) * 100}%` }} />
        ))}
        {jornada.actividad.map((t, i) => {
          const desde = minutosDelDia(t.desde);
          // Se suma la duración real al inicio (no se compara hora contra hora): un tramo de menos de
          // un minuto tiene el mismo hh:mm en ambos extremos y no es un cruce de medianoche.
          // Si cruza la medianoche, se recorta al fin del día.
          const duracionMin = Math.max(0, (new Date(t.hasta).getTime() - new Date(t.desde).getTime()) / 60000);
          const hasta = Math.min(1440, desde + duracionMin);
          return (
            <span
              key={i}
              className={`tramo ${t.tipo}`}
              style={{ left: `${(desde / 1440) * 100}%`, width: `${Math.max(0.3, ((hasta - desde) / 1440) * 100)}%` }}
              title={`${t.tipo === 'CONDUCCION' ? 'Conducción' : 'Detenido'} · ${fmtHora(t.desde)} a ${fmtHora(t.hasta)}`}
            />
          );
        })}
      </div>
      {conEje && <EjeHoras />}
    </div>
  );
}

/** Horas de conducción y km, con aviso (ícono + texto) si supera el límite visual. */
export function TotalesJornada({ jornada }: { jornada: JornadaChofer }) {
  const nivel = nivelExceso(jornada.horasConduccion);
  return (
    <div className="totales">
      <strong className={nivel === 'ok' ? '' : nivel === 'red' ? 'red' : 'amber'}>{fmtNum(jornada.horasConduccion, 1)} h de conducción</strong>
      <small>{fmtKm(jornada.km)}</small>
      {nivel !== 'ok' && (
        <span className={`aviso ${nivel === 'red' ? 'red' : 'amber'}`}>
          <TriangleAlert aria-hidden />
          Supera {LIMITE_CONDUCCION_H} h
        </span>
      )}
    </div>
  );
}
